import { Controller } from "@hotwired/stimulus"

// Shows or hides an event's location fields based on the kind chosen.
//
// The choice is now physical or virtual, driven by radios rather than a select
// of four. `address` and `text` were never a choice about the event — they were
// a choice about which field the author happened to have — so a physical event
// takes both a street address and the room within it, shown together.
//
// The legacy `select` target is still honoured so any form still rendering the
// old dropdown keeps working; when both are present the radios win, since they
// are what the current form posts.
//
// Usage:
//   <fieldset data-controller="location-type-toggle">
//     <input type="radio" value="address" data-location-type-toggle-target="kind"
//            data-action="change->location-type-toggle#onChange">
//     <input type="radio" value="virtual" data-location-type-toggle-target="kind"
//            data-action="change->location-type-toggle#onChange">
//     <div data-location-type-toggle-target="physical">…</div>
//   </fieldset>
export default class extends Controller {
  static targets = ["kind", "physical", "virtual", "select", "text", "address", "facility"]

  declare readonly kindTargets: HTMLInputElement[]
  declare readonly hasPhysicalTarget: boolean
  declare readonly physicalTarget: HTMLElement
  declare readonly hasVirtualTarget: boolean
  declare readonly virtualTarget: HTMLElement
  declare readonly selectTarget: HTMLSelectElement
  declare readonly hasSelectTarget: boolean
  declare readonly textTarget: HTMLElement
  declare readonly addressTarget: HTMLElement
  declare readonly facilityTarget: HTMLElement
  declare readonly hasTextTarget: boolean
  declare readonly hasAddressTarget: boolean
  declare readonly hasFacilityTarget: boolean

  connect() {
    this.sync()
  }

  onChange() {
    this.sync()
  }

  private sync() {
    const value = this.selectedValue()
    if (value === null) return

    // Current form: one block holding the address and the room within it, and
    // one holding the meeting link.
    this.toggle(this.hasPhysicalTarget ? this.physicalTarget : null, value !== "virtual")
    this.toggle(this.hasVirtualTarget ? this.virtualTarget : null, value === "virtual")

    // Legacy dropdown form.
    this.toggle(this.hasTextTarget ? this.textTarget : null,
                value === "virtual" || value === "text")
    this.toggle(this.hasAddressTarget ? this.addressTarget : null, value === "address")
    this.toggle(this.hasFacilityTarget ? this.facilityTarget : null, value === "facility")
  }

  private selectedValue(): string | null {
    const checked = this.kindTargets.find((radio) => radio.checked)
    if (checked) return checked.value
    // No radio checked yet is a real state — say nothing rather than collapsing
    // every field, which would hide the fields the author is mid-way through.
    if (this.kindTargets.length > 0) return null

    return this.hasSelectTarget ? this.selectTarget.value : null
  }

  // Hiding is not enough: a hidden input still posts. Disabling keeps the kind
  // that is not showing out of the submission, so switching to virtual does not
  // resubmit a room — and switching back finds it still there, because the
  // column it lives in was never written.
  private toggle(el: HTMLElement | null, show: boolean) {
    if (!el) return

    el.classList.toggle("hidden", !show)
    el.querySelectorAll<HTMLInputElement>("input, select, textarea").forEach((field) => {
      field.disabled = !show
    })
  }
}
